import { ProductType, SlidingDoorComponent, PlannerEcsMultiSystem, EventComponent, calculateLightDrivers } from '@inter-ikea-storageone/range';
import { HierarchyUtils, ComponentIds } from '@inter-ikea-core/core';
// When adding and removing lights from the scene this system will handle
// the amount of cords, driversand a controler that should be added to the shopping cart
export class PaxLightsAccessoriesSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'PaxLightsAccessoriesSystem', [
            ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID,
            EventComponent.id
        ]);
        this.tenWattDriver = {
            name: 'tenWattDriver',
            wattage: 10,
            productRef: '50356187',
            ports: 3
        };
        this.thirtyWattDriver = {
            name: 'thirtyWattDriver',
            wattage: 30,
            productRef: '60342656',
            ports: 9
        };
        this.powerSupplyCord3Point5Meters = '50446881';
        this.intermediateCord2meters = '30394699';
        this.oldControler = '70408595'; // TRÅDFRI
        this.controler = '20528128'; // RODRET
        // In some countries the articles above does not exists and we should try to use these ones instead
        this.anslutaPowerSupplyCord3Point5Meters = '50116593';
        this.anslutaIntermediateCord2meters = '50116729';
        /*
         * Hinge doors: door product ref
         * Sliding door: assembly product ref
         * Panel sldiding door: panel product ref
         */
        this.transparentDoorRefs = [
            '40336722', // DRAMMEN
            '80329201', // UNDREDAL
            '80329197', // TYSSEDAL
            '20329195', // TYSSEDAL
            '50160406', // BERGSBO
            '10552500', // TONSTAD
            '70480751', // BJÖRNÖYA
            '10480754',
            '60480756',
            '10480749',
            '10442352', // SEKKEN
            '70442349',
            '90442353',
            '00442343',
            '30442346',
            '30473578', // SVARTISDAL
            '60473572',
            '90473575',
            '20473569',
            '40545297', // GRIMO
            '00545303',
            '10545294',
            '60545300'
        ];
        this.nbrOfLightsInScene = 0;
        this.nbrOfDoorsInScene = 0;
        this.products = [];
    }
    onAdd(entity, newComponent) {
        if (newComponent.typeId === ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID) {
            if (HierarchyUtils.descendants(entity).filter(e => this.coreApi.entities.isProductType(e, ProductType.PAX_INNER_LIGHT) ||
                this.coreApi.entities.isProductType(e, ProductType.PAX_INNER_LIGHT_OVERSIDAN)).length > 0) {
                // Update when inner lights are being dragged since they can autoadapt to different sizes
                this.updateLights();
            }
        }
    }
    onUpdate(_entity, _oldComponent, newComponent) {
        if (newComponent.typeId === EventComponent.id &&
            ['load', 'history'].includes(newComponent.event)) {
            this.checkLightsInScene();
        }
    }
    onRemove() { }
    removeCurrentDriversAndCordsInScene() {
        const driversInScene = this.coreApi.entities.getByProductType(ProductType.LIGHT_DRIVER);
        const cordsInScene = this.coreApi.entities.getByProductType(ProductType.LIGHT_CORD);
        const controlersInScene = this.coreApi.entities.getByProductType(ProductType.LIGHT_CONTROLER);
        driversInScene.forEach(driver => this.coreApi.getServices().entityManager.disposeEntity(driver));
        cordsInScene.forEach(cord => this.coreApi.getServices().entityManager.disposeEntity(cord));
        controlersInScene.forEach(controler => this.coreApi.getServices().entityManager.disposeEntity(controler));
    }
    checkLightsInScene() {
        const currentLightsInScene = this.getLightsInScene();
        const currentDoorsInScene = this.getDoorsInScene();
        const controlersInScene = this.coreApi.entities.getByProductType(ProductType.LIGHT_CONTROLER);
        if (!this.shouldIncludeControler()) {
            controlersInScene.forEach(controler => this.coreApi.getServices().entityManager.disposeEntity(controler));
        }
        this.nbrOfLightsInScene = currentLightsInScene.length;
        this.nbrOfDoorsInScene = currentDoorsInScene.length;
        this.updateLights();
    }
    updateLights() {
        // Remove previous drivers and cords that could be in the scene
        this.removeCurrentDriversAndCordsInScene();
        if (this.nbrOfLightsInScene > 0) {
            const requiredDrivers = this.calculateNumberOfDrivers();
            const requiredDriversAmount = requiredDrivers.tenWattDriver + requiredDrivers.thirtyWattDriver;
            const requiredCords = this.calculateNumberOfCords(requiredDriversAmount);
            this.generateRequiredEntities(requiredDrivers, requiredCords);
        }
    }
    generateRequiredEntities(drivers, cords) {
        this.createEntitiesInScene(drivers.tenWattDriver, this.tenWattDriver.productRef, this.thirtyWattDriver.productRef);
        this.createEntitiesInScene(drivers.thirtyWattDriver, this.thirtyWattDriver.productRef);
        this.createEntitiesInScene(cords.powerSupplyCable35, this.powerSupplyCord3Point5Meters, this.anslutaPowerSupplyCord3Point5Meters);
        this.createEntitiesInScene(cords.intermediateCable2, this.intermediateCord2meters, this.anslutaIntermediateCord2meters);
        if (this.shouldIncludeControler())
            this.createEntitiesInScene(1, this.oldControler, this.controler);
    }
    isFrontTransparent(entity) {
        const doorType = this.coreApi.entities.getProductType(entity);
        const targetRefs = [];
        switch (doorType) {
            case ProductType.PAX_HINGE_DOOR:
                targetRefs.push(this.coreApi.entities.getProductRef(entity));
                break;
            case ProductType.PAX_SLIDING_DOOR_REGULAR_FRONT:
                const assemblies = this.coreApi.entities.getAncestorsWithProductType(entity, ProductType.PAX_SLIDING_DOOR_REGULAR_ASM);
                targetRefs.push(...assemblies.map(this.coreApi.entities.getProductRef));
                break;
            case ProductType.PAX_SLIDING_DOOR_PANEL_FRONT:
                const panels = this.coreApi.entities.getDescendantsWithProductType(entity, ProductType.PAX_SLIDING_DOOR_PANEL);
                targetRefs.push(...panels.map(this.coreApi.entities.getProductRef));
                break;
        }
        return targetRefs.filter(Boolean).some(ref => this.transparentDoorRefs.includes(ref));
    }
    shouldIncludeControler() {
        return (!!this.getInnerLights()
            .map(light => {
            const frontHits = this.coreApi.entities.getCoveringFronts(light);
            return (!frontHits.length || frontHits.some(({ entity }) => this.isFrontTransparent(entity)));
        })
            .filter(Boolean).length || this.getOuterLights().length > 0);
    }
    createEntitiesInScene(amount, productRef, backupRef) {
        const mainArticleExists = this.articleExistsInMarket(productRef);
        const backupArticleExists = backupRef && this.articleExistsInMarket(backupRef);
        const productRefToUse = mainArticleExists ? productRef : backupArticleExists ? backupRef : null;
        if (!productRefToUse)
            return;
        [...Array(amount)].forEach(() => {
            const entity = this.coreApi.getServices().productManager.createEntity(productRefToUse);
            if (entity)
                this.coreApi.entities.placementService.forcePlaceEntity(entity);
        });
    }
    calculateNumberOfDrivers() {
        const driversToUse = {
            tenWattDriver: 0,
            thirtyWattDriver: 0
        };
        const outerLights = this.getOuterLights().map(light => Object.assign(light, {
            wattage: this.getWattage(light),
            ports: 1 / 6
        }));
        const innerLights = this.getInnerLights().map(light => Object.assign(light, {
            wattage: this.getWattage(light),
            ports: 1
        }));
        const drivers = [this.tenWattDriver, this.thirtyWattDriver].map(d => Object.assign(d, { price: this.getArticlePrice(d.productRef) }));
        const combineDrivers = !this.getDoorsInScene().length;
        const lightGroups = combineDrivers
            ? [[...innerLights, ...outerLights]]
            : [innerLights, outerLights];
        lightGroups.forEach(grp => {
            const { tenWattDriver, thirtyWattDriver } = calculateLightDrivers(drivers, grp);
            driversToUse.tenWattDriver += tenWattDriver;
            driversToUse.thirtyWattDriver += thirtyWattDriver;
        });
        return driversToUse;
    }
    calculateNumberOfCords(driverAmount) {
        const cordsToUse = {
            powerSupplyCable35: 0,
            intermediateCable2: 0
        };
        if (!driverAmount)
            return cordsToUse;
        cordsToUse.powerSupplyCable35 = 1;
        cordsToUse.intermediateCable2 = driverAmount - 1;
        return cordsToUse;
    }
    getLightsInScene() {
        const allLightsInScene = [];
        const currentLightsInScene = this.coreApi.entities.getByComponent(ComponentIds.LIGHT_COMPONENT_ID);
        if (currentLightsInScene) {
            allLightsInScene.push(...currentLightsInScene);
        }
        return allLightsInScene;
    }
    getDoorsInScene() {
        const allDoorsInScene = [];
        const currentDoorsInScene = this.coreApi.entities.getByComponents([
            ComponentIds.DOOR_COMPONENT_ID,
            SlidingDoorComponent.id
        ]);
        if (currentDoorsInScene) {
            allDoorsInScene.push(...currentDoorsInScene);
        }
        return allDoorsInScene;
    }
    getOuterLights() {
        const allLights = this.getLightsInScene();
        return allLights.filter((e) => e.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'outer');
    }
    getInnerLights() {
        const allLights = this.getLightsInScene();
        return allLights.filter((e) => e.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'inner' ||
            e.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'inner-oversidan');
    }
    articleExistsInMarket(articleId) {
        return this.products.some(p => p.coreProductId === articleId);
    }
    getWattage(light) {
        const product = this.products.find((product) => product.coreProductId ===
            light.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef);
        return product?.wattage || 0;
    }
    getArticlePrice(articleId) {
        return this.products.find((product) => product.coreProductId === articleId)
            ?.priceInformation.currentPrice;
    }
    setMarketProducts(marketProduct) {
        this.products = marketProduct;
        this.updateLights();
    }
}
//# sourceMappingURL=PaxLightsAccessoriesSystem.js.map